import {
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { getFirebaseAuth } from "@/lib/firebase/client";
import { getAppCheckHeader } from "@/lib/firebase/appCheck";

const FUNCTION_BASE =
  import.meta.env.VITE_FUNCTIONS_BASE_URL ||
  "https://europe-central2-kidsduty-53b67.cloudfunctions.net";

/**
 * Usuwa konto pojedynczego usera (sam siebie kasuje). Rodzina i jej dane
 * zostają — kasujemy tylko members/{uid}, users/{uid}, fcmTokens i Auth
 * user. Historyczne completions/transactions/payouts zachowane (audit
 * trail), ale w UI displayName autora będzie pusty.
 *
 * Guards po stronie CF:
 *   - balance > 0 → "Najpierw poproś rodzica o wypłatę" (code: has-balance)
 *   - sole admin → "Promuj drugiego admina pierwsze" (code: sole-admin)
 *   - reauth required (auth_time ≤ 5min) — klient robi reauth z password
 *
 * Po sukcesie: klient powinien wylogować się i przekierować na /login.
 */
export async function deleteSelfAccount(input: {
  password: string;
}): Promise<{ ok: true }> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Nie zalogowano");
  if (!user.email) throw new Error("Konto bez emaila — skontaktuj się z supportem");

  // Reauth — wymusza świeży idToken (auth_time = teraz).
  const cred = EmailAuthProvider.credential(user.email, input.password);
  try {
    await reauthenticateWithCredential(user, cred);
  } catch (e) {
    const code = (e as { code?: string })?.code;
    if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
      throw new Error("Błędne hasło");
    }
    throw e;
  }

  const token = await user.getIdToken(true);
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/deleteSelfMember`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body?.error as string) || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Trwale usuwa konto rodziny z zachowaniem zanonimizowanych statystyk
 * (kwoty, typy zadań, cele) w `archivedHouseholds/{archivedId}`.
 *
 * Reauth: tuż przed wywołaniem klient robi `reauthenticateWithCredential`
 * z email+hasło → świeży idToken. Backend sprawdza `auth_time` ≤ 5 min.
 *
 * Po sukcesie: klient powinien wylogować się i przekierować na /login.
 * Operacja jest nieodwracalna — nawet admin nie ma jak przywrócić konta.
 */
export async function deleteHouseholdAnonymized(input: {
  slug: string;
  password: string;
}): Promise<{ ok: true; archivedId: string }> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Nie zalogowano");
  if (!user.email) throw new Error("Konto bez emaila — skontaktuj się z supportem");

  // Reauth — wymusza świeży idToken (auth_time = teraz).
  const cred = EmailAuthProvider.credential(user.email, input.password);
  try {
    await reauthenticateWithCredential(user, cred);
  } catch (e) {
    const code = (e as { code?: string })?.code;
    if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
      throw new Error("Błędne hasło");
    }
    throw e;
  }

  // Świeży token po reauth.
  const token = await user.getIdToken(true);
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/deleteHouseholdAnonymized`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify({ slug: input.slug }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body?.error as string) || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Sprawdza dostępność slug rodziny (dryRun) lub atomicznie ją rezerwuje.
 * Tylko rodzic. Slug jest LOCKED po rezerwacji — zmiana złamałaby
 * fake-email logowania dzieci.
 *
 * R0.6 audyt refresh 2026-05-13: real claim zwraca tylko `unavailable`
 * (generic) zamiast rozróżnienia "taken" vs "already-set" — anti-enumeration.
 * `household-not-found` zostawione (legit błąd integralności) ale niemożliwy
 * w normalnym flow (admin musi mieć HH).
 */
export async function claimHouseholdSlug(
  slug: string,
  opts?: { dryRun?: boolean },
): Promise<
  | { ok: true; available?: boolean }
  | {
      ok: false;
      reason: "unavailable" | "household-not-found";
    }
  | { available: boolean }
> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("Nie zalogowano");
  const token = await user.getIdToken();
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/claimHouseholdSlug`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify({ slug, dryRun: opts?.dryRun }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body?.error as string) || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Nadaje / zmienia username dziecka. Migruje Firebase Auth email
 * istniejącego konta na fake-email `<username>@<slug>.kids.sianko.local`,
 * dzięki czemu dziecko może logować się przez username+slug+hasło.
 *
 * Stary email zostaje uwolniony (mogłby być re-used przez kogoś).
 * Po migracji dziecko nie zaloguje się już swoim oryginalnym mailem.
 */
export async function setMemberUsername(
  targetUid: string,
  username: string,
): Promise<{ ok: true; email: string }> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("Nie zalogowano");
  const token = await user.getIdToken();
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/setMemberUsername`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify({ targetUid, username }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body?.error as string) || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Wywołuje Cloud Function setMemberPassword — rodzic ustawia nowe hasło
 * domowniku (dziecku lub drugiemu rodzicowi). Admin SDK po stronie serwera
 * pozwala zmienić hasło bez znajomości starego.
 *
 * Zabezpieczenia (server):
 *  - caller musi mieć role "parent" w users/{uid}
 *  - target musi być w tym samym household
 *  - nie można zmienić hasła sobie (server odrzuca)
 */
export async function setMemberPassword(
  targetUid: string,
  newPassword: string,
): Promise<void> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("Nie zalogowano");
  const token = await user.getIdToken();
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/setMemberPassword`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify({ targetUid, newPassword }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = (body?.error as string) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
}

/**
 * Atomowy bootstrap rodzica + nowego household. Wywoływane w Signup.tsx
 * zaraz po `createUserWithEmailAndPassword` / `signInWithPopup`.
 *
 * Po CRIT-1 lockdown (FAZA 3 audytu 2026-05-12) klient nie może już sam
 * pisać do `users/{uid}` z dowolnym `householdId` — bootstrap MUSI przejść
 * przez tę CF która jako Admin SDK omija reguły.
 *
 * 409 z `code: "already-bootstrapped"` oznacza że user już ma userDoc —
 * caller powinien po prostu nawigować na /dashboard zamiast traktować jako błąd.
 *
 * R0.5 audyt refresh 2026-05-13: 403 z `code: "email-not-verified"`
 * gdy email/password user nie kliknął jeszcze linku weryfikacyjnego.
 * `BootstrapEmailNotVerifiedError` to dedykowana klasa żeby Signup.tsx mógł
 * `instanceof`-em przełączyć na ekran "sprawdź skrzynkę" zamiast generic
 * error toast.
 */
export class BootstrapEmailNotVerifiedError extends Error {
  readonly code = "email-not-verified" as const;
  constructor(message: string) {
    super(message);
    this.name = "BootstrapEmailNotVerifiedError";
  }
}

export async function bootstrapHousehold(input: {
  householdName: string;
  parentDisplayName: string;
}): Promise<{ ok: true; householdId: string }> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("Nie zalogowano");
  // Force-refresh idToken — po `user.reload()` claim `email_verified` w
  // poprzednim tokenie jest stale. Bootstrap-CF czyta świeży `email_verified`
  // przez `getAuth().verifyIdToken` więc musi dostać nowy token.
  const token = await user.getIdToken(true);
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/bootstrapHousehold`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify(input),
  });
  if (res.status === 403) {
    const body = await res.json().catch(() => null);
    if (body?.code === "email-not-verified") {
      throw new BootstrapEmailNotVerifiedError(
        body?.error || "Najpierw potwierdź adres email",
      );
    }
  }
  if (res.status === 409) {
    const body = await res.json().catch(() => null);
    // Już zbootstrappowany — caller nawiguje na /dashboard.
    if (body?.code === "already-bootstrapped" && body.householdId) {
      return { ok: true, householdId: body.householdId };
    }
    throw new Error(body?.error || "Konto już istnieje");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body?.error as string) || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Generuje jednorazowy kod logowania dziecka (Firebase custom token).
 * QR z tym tokenem loguje dziecko przez signInWithCustomToken bez
 * wpisywania czegokolwiek. Token ważny maks. 1h, nie zawiera hasła.
 *
 * Zabezpieczenia (server): caller parent, target child z tego samego
 * household, rate limit 5/min.
 */
export async function mintChildLoginToken(
  childUid: string,
): Promise<{ token: string }> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("Nie zalogowano");
  const token = await user.getIdToken();
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/mintChildLoginToken`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify({ childUid }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error((body?.error as string) || `HTTP ${res.status}`);
  }
  return res.json();
}

/**
 * Wywołuje Cloud Function selfHealMember — naprawia brakujący/niespójny
 * member doc dla zalogowanego rodzica (Admin SDK omija rules, więc
 * działa też gdy klient sam by nie mógł zmienić własnego role).
 *
 * Zwraca akcję: "noop" (nic nie trzeba), "patched" (zaktualizowano role/
 * isAdmin) lub "created" (utworzono brakujący doc).
 */
export async function selfHealMember(): Promise<{
  ok: true;
  action: "noop" | "patched" | "created";
}> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("Nie zalogowano");
  const token = await user.getIdToken();
  const appCheck = await getAppCheckHeader();
  const res = await fetch(`${FUNCTION_BASE}/selfHealMember`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...appCheck,
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const msg = (body?.error as string) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return res.json();
}
